import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { MaskedLines } from "./Reveal";
import { ACADEMY, WHATSAPP_LINK } from "../../lib/constants";

export default function Hero({ settings }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const title = settings?.hero_title || ACADEMY.name;
  const subtitle = settings?.hero_subtitle || "Where Passion Meets Performance Through Dance";
  const image = settings?.hero_image || "/hero.jpg";

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-purple grain"
      data-testid="hero-section"
    >
      {/* Parallax background image */}
      <motion.div style={{ y: imgY }} className="absolute inset-0 -z-0">
        <img
          src={image}
          alt="Classical dancer performing on stage"
          className="h-[120%] w-full object-cover object-[68%_center] opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A0F2E] via-[#1A0F2E]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A0F2E] via-transparent to-[#1A0F2E]/30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_35%,rgba(212,175,55,0.14),transparent_55%)]" />
      </motion.div>

      {/* Content */}
      <motion.div style={{ y: overlayY, opacity: fade }} className="relative z-10 rb-container flex min-h-[100svh] flex-col justify-center pt-28 pb-16">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="overline text-gold mb-6 flex items-center gap-3"
          data-testid="hero-tagline"
        >
          <span className="h-px w-10 bg-gold inline-block" />
          {settings?.hero_tagline || "Classical & Western Dance Institute"}
        </motion.p>

        <h1 className="font-serif font-light text-[#F6F4EE] text-5xl sm:text-7xl lg:text-[8.5rem] leading-[0.92] tracking-tighter">
          <MaskedLines lines={["Rythmbara"]} delay={0.3} lineClass="gold-gradient-text" />
          <MaskedLines lines={["Dance Academy"]} delay={0.5} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-8 max-w-xl text-lg md:text-xl text-[#F6F4EE]/80 leading-relaxed font-light"
          data-testid="hero-subtitle"
        >
          {subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a href="/contact" data-testid="hero-join-btn" className="btn-gold inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em]">
            Join Now <ArrowRight className="h-4 w-4" />
          </a>
          <a href={`tel:${ACADEMY.phoneRaw}`} data-testid="hero-call-btn" className="btn-outline-gold inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em]">
            <Phone className="h-4 w-4" /> Call Now
          </a>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn" className="btn-outline-gold inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em]">
            WhatsApp
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#F6F4EE]/60"
      >
        <span className="overline text-[10px]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="h-10 w-px bg-gradient-to-b from-gold to-transparent"
        />
      </motion.div>
    </section>
  );
}
